<script setup lang="ts">
  import { computed, inject, type Ref } from 'vue'
  import { Select } from 'ant-design-vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { CustomReportField } from '../models/CustomReportField'
  import { CustomReportFieldType } from '../models/CustomReportFieldType'
  import { ICustomReportSelectMappingFieldTypeArgs } from '../models/CustomReportFieldTypeArgs'

  interface ICustomReportSelectMappingConfigurationProps {
    modelValue?: string | string[] | null
    field: CustomReportField
    placeholder?: string
    localConfiguration: Record<string, any>
  }

  const props = defineProps<ICustomReportSelectMappingConfigurationProps>()
  const emit = defineEmits<{ 'update:modelValue': [string | string[] | null] }>()

  const rootConfiguration = inject<Ref<Record<string, any>>>('rootConfiguration')

  const { controlSize } = useWindowSize()

  const isMulti = computed(
    () => props.field.field_type === CustomReportFieldType.MultiSelectMapping
  )

  /**
   * Resolves a dot-notation absolute path against the root configuration object.
   * e.g. "analyses" or "sections.items"
   */
  function resolvePath(obj: Record<string, any>, path: string): unknown {
    return path.split('.').reduce<unknown>((current, key) => {
      if (current == null || typeof current !== 'object') return undefined
      return (current as Record<string, any>)[key]
    }, obj)
  }

  const mappedOptions = computed<Array<{ label: string; value: string }>>(() => {
    const args = props.field.field_type_args as ICustomReportSelectMappingFieldTypeArgs | undefined

    if (!args?.select_mapping_src_options_path) {
      throw new Error(
        `[CustomReportSelectMappingConfiguration] field "${props.field.name}" is missing field_type_args.select_mapping_src_options_path`
      )
    }
    if (!args.select_mapping_label_path) {
      throw new Error(
        `[CustomReportSelectMappingConfiguration] field "${props.field.name}" is missing field_type_args.select_mapping_label_path`
      )
    }
    if (!args.select_mapping_value_path) {
      throw new Error(
        `[CustomReportSelectMappingConfiguration] field "${props.field.name}" is missing field_type_args.select_mapping_value_path`
      )
    }

    const isRelative = args.select_mapping_src_relative === true

    if (!isRelative && !rootConfiguration?.value) {
      throw new Error(
        `[CustomReportSelectMappingConfiguration] No root configuration found. Ensure CustomReportConfiguration at configurationLevel === 1 is an ancestor of this component.`
      )
    }

    const sourceConfig = isRelative ? props.localConfiguration : rootConfiguration!.value
    const sourceArray = resolvePath(sourceConfig, args.select_mapping_src_options_path)

    if (!Array.isArray(sourceArray)) {
      throw new Error(
        `[CustomReportSelectMappingConfiguration] Path "${args.select_mapping_src_options_path}" did not resolve to an array on the root configuration. Resolved value: ${JSON.stringify(sourceArray)}`
      )
    }

    if (sourceArray.length === 0) {
      return []
    }

    const firstItem = sourceArray[0]
    if (firstItem == null || typeof firstItem !== 'object') {
      throw new Error(
        `[CustomReportSelectMappingConfiguration] Items in "${args.select_mapping_src_options_path}" are not objects. Cannot map label/value paths.`
      )
    }
    if (!(args.select_mapping_label_path in firstItem)) {
      throw new Error(
        `[CustomReportSelectMappingConfiguration] label_path "${args.select_mapping_label_path}" does not exist on items in "${args.select_mapping_src_options_path}"`
      )
    }
    if (!(args.select_mapping_value_path in firstItem)) {
      throw new Error(
        `[CustomReportSelectMappingConfiguration] value_path "${args.select_mapping_value_path}" does not exist on items in "${args.select_mapping_src_options_path}"`
      )
    }

    return sourceArray.map((item: Record<string, any>) => ({
      label: String(item[args.select_mapping_label_path] ?? ''),
      value: String(item[args.select_mapping_value_path] ?? '')
    }))
  })

  const handleChange = (val: string | string[]) => {
    emit('update:modelValue', val ?? null)
  }
</script>

<template>
  <Select
    :value="modelValue"
    :mode="isMulti ? 'multiple' : undefined"
    :placeholder="placeholder || `Select ${field.display_name.toLowerCase()}`"
    :size="controlSize"
    :disabled="field.readonly"
    :options="mappedOptions"
    class="w-full"
    showSearch
    allowClear
    @change="handleChange"
  />
</template>
